import { Injectable, inject } from '@angular/core';
import { RegistrationSharedService } from '../registration/registration-shared.service';
import { ANALYTICS_EVENT } from '../../constants';
import { environment } from '../../../environments/environment';
import { getCategorySlugFromName } from '../../utils/category-mapping';

declare const fbq: any;
declare const dataLayer: unknown[];
declare const gtag: any;

interface FBPixelEventData {
  content_category?: string;
  content_ids?: string[];
  content_type?: string;
  content_name?: string;
  value?: number;
  currency?: string;
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private registrationSharedService = inject(RegistrationSharedService);

  constructor() {}

  private getCategorySlug(): string {
    const categories = this.registrationSharedService.registrationData.establishment.categories;
    if (!categories || categories.length === 0) {
      return 'otros';
    }

    const slug = getCategorySlugFromName(categories[0]);

    if (slug === 'otros' && this.registrationSharedService.customCategoryText) {
      return this.registrationSharedService.customCategoryText;
    }

    return slug;
  }


  private isFbqAvailable(): boolean {
    return typeof fbq !== 'undefined' && fbq !== null;
  }

  private sendEvent = (event: string, data: Record<string, unknown>) => {
    try {
      dataLayer.push({
        event,
        ...data,
      });
    } catch (error) {
      console.log({ error });
    }
  };

  createSubscription() {
    const valuesPurchaseEvent = this.valuesPurchaseEvent;
    const { registrationData } = this.registrationSharedService;

    if (!this.isFbqAvailable()) {
      console.log('Facebook Pixel not available');
      return;
    }

    try {
      const eventData: FBPixelEventData = {
        content_category: this.getCategorySlug(),
        content_type: 'subscription',
        content_name: `${registrationData.planType} Plan`,
        value: valuesPurchaseEvent.value,
        currency: valuesPurchaseEvent.currency
      };

      fbq('track', 'Purchase', eventData);
      console.log('FB Pixel Purchase event sent:', eventData);

      this.sendEvent(ANALYTICS_EVENT.SUBSCRIPTION_SIGN_UP, {
        establishment_name: registrationData.establishment.slug,
        billing_type: registrationData.billingType,
        signup_email: registrationData.customer.email,
        signup_phone_number: registrationData.customer.phone,
        signup_country: registrationData.establishment.countryCode,
        plan_type: registrationData.planType,
        establishment_category: this.getCategorySlug(),
        ...valuesPurchaseEvent,
      });

      gtag('event', 'conversion', {
        send_to: environment.adsConversionId,
        ...valuesPurchaseEvent,
      });
    } catch (error) {
      console.error('Error sending FB Pixel Purchase event:', error);
    }
  }

  private get valuesPurchaseEvent() {
    const establishmentCountrycode =
      this.registrationSharedService.registrationData.establishment.countryCode;
    const billingType = this.registrationSharedService.registrationData.billingType;
    const currencyCodes = {
      AR: 'ARS',
      CL: 'CLP',
      UY: 'UYU',
      XX: 'USD',
    };

    return {
      currency: currencyCodes[establishmentCountrycode],
      value:
        +this.registrationSharedService.selectedPlan.amounts[billingType][establishmentCountrycode],
    };
  }

  nextStep(stepName: string) {
    try {
      this.sendEvent(ANALYTICS_EVENT.SUBSCRIPTION_NEXT, {
        subscription_section: stepName,
      });
    } catch {
    }
  }

  previousStep(stepName: string) {
    try {
      this.sendEvent(ANALYTICS_EVENT.SUBSCRIPTION_PREVIOUS, {
        subscription_section: stepName,
      });
    } catch {
    }
  }

  startedSubscription() {
    try {
      this.sendEvent(ANALYTICS_EVENT.SUBSCRIPTION_STARTED, {});
    } catch {
    }
  }

  startedCheckout() {
    if (!this.isFbqAvailable()) {
      console.log('Facebook Pixel not available');
      return;
    }

    try {
      const valuesPurchaseEvent = this.valuesPurchaseEvent;
      const { registrationData } = this.registrationSharedService;

      const eventData: FBPixelEventData = {
        content_category: this.getCategorySlug(),
        content_type: 'subscription',
        content_name: `${registrationData.planType} Plan`,
        value: valuesPurchaseEvent.value,
        currency: valuesPurchaseEvent.currency
      };

      fbq('track', 'InitiateCheckout', eventData);
      console.log('FB Pixel InitiateCheckout event sent:', eventData);

      this.sendEvent(ANALYTICS_EVENT.SUBSCRIPTION_PAYMENT, {
        establishment_category: this.getCategorySlug()
      });
    } catch (error) {
      console.error('Error sending FB Pixel InitiateCheckout event:', error);
    }
  }

  addedPaymentInfo() {
    if (!this.isFbqAvailable()) {
      console.log('Facebook Pixel not available');
      return;
    }

    try {
      const eventData: FBPixelEventData = {
        content_category: this.getCategorySlug()
      };

      fbq('track', 'AddPaymentInfo', eventData);
      console.log('FB Pixel AddPaymentInfo event sent:', eventData);

      this.sendEvent(ANALYTICS_EVENT.SUBSCRIPTION_ADD_PAYMENT_INFO, {
        establishment_category: this.getCategorySlug()
      });
    } catch (error) {
      console.error('Error sending FB Pixel AddPaymentInfo event:', error);
    }
  }

  sendReactivateSubscriptionEvent({ slug, reactivateError }) {
    try {
      this.sendEvent(ANALYTICS_EVENT.RESUBSCRIPTION, { slug, reactivateError });
    } catch (error) {
      // El bloque catch está vacío de forma intencional.
    }
  }
}
